import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../providers/authProvider";
import { useData } from "../providers/dataProvider";
import { CONTEXTS, Context as TodoContext, TodoData } from "../../../types";
import { FeedbackWidget } from "./FeedbackWidget";
import { AdminFeedbackPanel } from "./AdminFeedbackPanel";

const getTodayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const CAPTURE_CONTEXT_KEY = "todoit-capture-context";

const getStoredCaptureContext = (): TodoContext => {
  try {
    const stored = window.localStorage.getItem(CAPTURE_CONTEXT_KEY);
    if (stored === CONTEXTS.WORK || stored === CONTEXTS.HOME) return stored;
  } catch {}
  return CONTEXTS.WORK;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const byDoneAtDesc = (a: TodoData, b: TodoData) => (b.doneAt || "").localeCompare(a.doneAt || "");

const matchesQuery = (todo: TodoData, words: string[]) => {
  const haystack = `${todo.title} ${todo.notes || ""}`.toLowerCase();
  return words.every((w) => haystack.includes(w));
};

export const Dashboard = () => {
  const { logout } = useAuth();
  const { todos, loading, createTodo, updateTodo, deleteTodo } = useData();
  const [contextFilter, setContextFilter] = useState<TodoContext | "all">("all");
  const [captureContext, setCaptureContext] = useState<TodoContext>(getStoredCaptureContext);
  const [title, setTitle] = useState("");
  const [childDrafts, setChildDrafts] = useState<Record<string, string>>({});
  const [addingChildTo, setAddingChildTo] = useState<string | null>(null);
  const [collapsedParents, setCollapsedParents] = useState<Record<string, boolean>>({});
  const [expandedDoneChildren, setExpandedDoneChildren] = useState<Record<string, boolean>>({});
  const [expandedCompletedChildren, setExpandedCompletedChildren] = useState<
    Record<string, boolean>
  >({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const today = getTodayLocal();

  // "/" focuses search from anywhere (unless already typing somewhere else);
  // Escape clears it when it's focused.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT";
      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === "Escape" && document.activeElement === searchInputRef.current) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggleCollapsed = (id: string) =>
    setCollapsedParents((c) => ({ ...c, [id]: !c[id] }));
  const toggleDoneChildrenVisible = (id: string) =>
    setExpandedDoneChildren((c) => ({ ...c, [id]: !c[id] }));
  const toggleCompletedChildrenVisible = (id: string) =>
    setExpandedCompletedChildren((c) => ({ ...c, [id]: !c[id] }));

  const setCaptureContextPersisted = (context: TodoContext) => {
    setCaptureContext(context);
    try {
      window.localStorage.setItem(CAPTURE_CONTEXT_KEY, context);
    } catch {}
  };

  const visibleTodos = useMemo(
    () =>
      contextFilter === "all"
        ? todos
        : todos.filter((t) => t.context === contextFilter),
    [todos, contextFilter],
  );

  // A todo with children acts as a project; one with none is a plain leaf.
  // Nesting is capped at one level, enforced only here in the UI (a todo
  // that already has children is never offered as something to nest
  // further, and the picker for "which parent" only exists at top level).
  const childrenOf = (parentId: string) =>
    visibleTodos.filter((t) => t.parentId === parentId).sort((a, b) => a.order - b.order);

  const topLevelTodos = useMemo(
    () => visibleTodos.filter((t) => !t.parentId),
    [visibleTodos],
  );

  const hasChildren = (id: string) => visibleTodos.some((c) => c.parentId === id);

  // Active project sections — a project drops out of this the moment it's
  // itself marked done (however that happened: manually, or by "work on
  // this today" falling back to scheduling the parent once every step was
  // done), and moves to Completed below instead of sitting there empty with
  // no way to close it out.
  const parents = useMemo(
    () => topLevelTodos.filter((t) => !t.done && hasChildren(t.id)),
    [topLevelTodos, visibleTodos],
  );

  const inboxTodos = useMemo(
    () =>
      topLevelTodos
        .filter((t) => !t.done && !t.scheduledDate && !hasChildren(t.id))
        .sort((a, b) => a.order - b.order),
    [topLevelTodos, visibleTodos],
  );

  // Completed, top-level only — any done top-level todo, project or plain
  // leaf alike. A done child of a still-active project stays inside that
  // project's own collapsed "completed" sublist instead of duplicating here.
  const completedTopLevel = useMemo(
    () => topLevelTodos.filter((t) => t.done).sort(byDoneAtDesc),
    [topLevelTodos],
  );

  const todayTodos = useMemo(
    () =>
      visibleTodos
        .filter((t) => !t.done && t.scheduledDate && t.scheduledDate <= today)
        .sort((a, b) => a.order - b.order),
    [visibleTodos, today],
  );

  // Deferred 2+ times (scheduled/rescheduled without finishing) and not
  // already sitting in Today (which shows its own "deferred N×" badge
  // instead of duplicating the row here).
  const neededAttention = useMemo(
    () =>
      visibleTodos
        .filter(
          (t) =>
            !t.done &&
            t.timesScheduled >= 2 &&
            !(t.scheduledDate && t.scheduledDate <= today),
        )
        .sort((a, b) => b.timesScheduled - a.timesScheduled),
    [visibleTodos, today],
  );

  // Search ignores the context filter (it's meant to find anything,
  // anywhere) — a context tag on each row makes up for that.
  const searchResults = useMemo(() => {
    const words = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const matches = todos.filter((t) => matchesQuery(t, words));
    const open = matches.filter((t) => !t.done).sort((a, b) => a.order - b.order);
    const done = matches.filter((t) => t.done).sort(byDoneAtDesc);
    return [...open, ...done];
  }, [todos, searchQuery]);

  // On the "all" tab you pick a context per capture (and it's remembered);
  // on the work/home tab there's nothing to pick — it's just that context.
  const capture = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const context = contextFilter === "all" ? captureContext : contextFilter;
    await createTodo({ title: title.trim(), context });
    setTitle("");
  };

  // Also used to "bump" an overdue rolled-over item back to literal today —
  // TodoList shows this same action either way, just labeled differently.
  const scheduleToday = (todo: TodoData) => {
    updateTodo(todo, {
      scheduledDate: today,
      timesScheduled: todo.timesScheduled + 1,
    });
  };

  const removeFromToday = (todo: TodoData) => {
    updateTodo(todo, { scheduledDate: null });
  };

  const resetDeferred = (todo: TodoData) => {
    updateTodo(todo, { timesScheduled: 0 });
  };

  const getParentTitle = (todo: TodoData) =>
    todo.parentId ? todos.find((t) => t.id === todo.parentId)?.title : undefined;

  const getContextTag = (todo: TodoData) => todo.context;

  const editTodo = (
    todo: TodoData,
    updates: { title: string; priority: number | null; complexity: number | null },
  ) => {
    updateTodo(todo, updates);
  };

  const addChild = async (parent: TodoData, e: FormEvent) => {
    e.preventDefault();
    const text = (childDrafts[parent.id] || "").trim();
    if (!text) return;
    await createTodo({ title: text, context: parent.context, parentId: parent.id });
    setChildDrafts((d) => ({ ...d, [parent.id]: "" }));
  };

  // "Work on this today": the top open, unscheduled direct child — or, if
  // there are none left, the parent itself (per the "no open steps left"
  // case — scheduling the parent is a reasonable default over blocking).
  const scheduleTopOf = (parent: TodoData) => {
    const openChildren = childrenOf(parent.id).filter((c) => !c.done && !c.scheduledDate);
    const target = openChildren[0] ?? parent;
    updateTodo(target, {
      scheduledDate: today,
      timesScheduled: target.timesScheduled + 1,
    });
  };

  const backgroundClass =
    contextFilter === CONTEXTS.WORK
      ? "bg-sky-50"
      : contextFilter === CONTEXTS.HOME
        ? "bg-amber-50"
        : "bg-white";

  const searching = searchQuery.trim().length > 0;

  return (
    <div className={`min-h-screen transition-colors ${backgroundClass}`}>
    <div className="max-w-2xl mx-auto p-6 space-y-8">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">todoit</h1>
        <div className="flex items-center gap-2">
          {(["all", CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
            <button
              key={c}
              onClick={() => setContextFilter(c)}
              className={`px-3 py-1 rounded text-sm capitalize ${
                contextFilter === c
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-700"
              }`}
            >
              {c}
            </button>
          ))}
          <button onClick={logout} className="text-sm text-neutral-500 ml-2">
            Log out
          </button>
        </div>
      </header>

      <div className="relative">
        <input
          ref={searchInputRef}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="Search everything… (press / to focus)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searching && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400"
          >
            clear
          </button>
        )}
      </div>

      <form onSubmit={capture} className="flex gap-2">
        <input
          className="flex-1 border rounded px-3 py-2"
          placeholder="Throw something in…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {contextFilter === "all" && (
          <div className="flex rounded overflow-hidden border">
            {([CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCaptureContextPersisted(c)}
                className={`px-2 py-1 text-xs capitalize ${
                  captureContext === c
                    ? "bg-neutral-900 text-white"
                    : "bg-neutral-100 text-neutral-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <button className="bg-neutral-900 text-white rounded px-4">Add</button>
      </form>

      {loading ? (
        <p className="text-neutral-500">Loading…</p>
      ) : searching ? (
        <section>
          <h2 className="text-sm font-semibold text-neutral-500 mb-2">
            Search results ({searchResults.length})
          </h2>
          <TodoList
            todos={searchResults}
            onToggle={(t) => updateTodo(t, { done: !t.done })}
            onDelete={deleteTodo}
            onSchedule={scheduleToday}
            onUnschedule={removeFromToday}
            onEdit={editTodo}
            getParentTitle={getParentTitle}
            getContextTag={getContextTag}
            today={today}
            empty="No matches."
          />
        </section>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold text-neutral-500 mb-2">Today</h2>
            <TodoList
              todos={todayTodos}
              onToggle={(t) => updateTodo(t, { done: !t.done })}
              onDelete={deleteTodo}
              onSchedule={scheduleToday}
              onUnschedule={removeFromToday}
              onEdit={editTodo}
              getParentTitle={getParentTitle}
              today={today}
              empty="Nothing scheduled yet."
            />
          </section>

          {neededAttention.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-neutral-500 mb-2">
                Needs attention ({neededAttention.length})
              </h2>
              <TodoList
                todos={neededAttention}
                onToggle={(t) => updateTodo(t, { done: !t.done })}
                onDelete={deleteTodo}
                onSchedule={scheduleToday}
                onEdit={editTodo}
                onReset={resetDeferred}
                getParentTitle={getParentTitle}
                today={today}
                empty=""
              />
            </section>
          )}

          <section>
            <h2 className="text-sm font-semibold text-neutral-500 mb-2">Inbox</h2>
            {!inboxTodos.length ? (
              <p className="text-sm text-neutral-400">Inbox is empty.</p>
            ) : (
              <ul className="space-y-1">
                {inboxTodos.map((todo) => (
                  <li key={todo.id} className="bg-white border rounded">
                    <div className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={todo.done}
                        onChange={() => updateTodo(todo, { done: !todo.done })}
                      />
                      <EditableTodo todo={todo} onEdit={editTodo} className="text-sm" />
                      {!todo.scheduledDate && (
                        <button
                          onClick={() => scheduleToday(todo)}
                          className="text-xs text-neutral-400"
                        >
                          today
                        </button>
                      )}
                      <button
                        onClick={() =>
                          setAddingChildTo(addingChildTo === todo.id ? null : todo.id)
                        }
                        className="text-xs text-neutral-400"
                      >
                        break down
                      </button>
                      <button
                        onClick={() => deleteTodo(todo)}
                        className="text-xs text-neutral-400"
                      >
                        delete
                      </button>
                    </div>
                    {addingChildTo === todo.id && (
                      <form
                        onSubmit={(e) => addChild(todo, e)}
                        className="flex gap-2 px-3 pb-2"
                      >
                        <input
                          className="flex-1 border rounded px-2 py-1 text-sm"
                          placeholder="First step…"
                          value={childDrafts[todo.id] || ""}
                          onChange={(e) =>
                            setChildDrafts((d) => ({ ...d, [todo.id]: e.target.value }))
                          }
                          autoFocus
                        />
                        <button className="text-sm bg-neutral-100 rounded px-2">Add</button>
                      </form>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {parents.map((parent) => {
            const allChildren = childrenOf(parent.id);
            const openChildren = allChildren.filter((c) => !c.done);
            const doneChildren = allChildren.filter((c) => c.done).sort(byDoneAtDesc);
            const collapsed = collapsedParents[parent.id];
            return (
              <section key={parent.id}>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <button
                      onClick={() => toggleCollapsed(parent.id)}
                      className="text-xs text-neutral-500 shrink-0"
                    >
                      {collapsed ? "▸" : "▾"}
                    </button>
                    <EditableTodo
                      todo={parent}
                      onEdit={editTodo}
                      className="text-sm font-semibold text-neutral-500"
                    />
                  </div>
                  <button
                    onClick={() => scheduleTopOf(parent)}
                    className="text-xs text-neutral-500 underline shrink-0"
                  >
                    Work on this today
                  </button>
                </div>
                {!collapsed && (
                  <>
                    <TodoList
                      todos={openChildren}
                      onToggle={(t) => updateTodo(t, { done: !t.done })}
                      onDelete={deleteTodo}
                      onSchedule={scheduleToday}
                      onEdit={editTodo}
                      today={today}
                      empty="Nothing here yet."
                    />
                    {doneChildren.length > 0 && (
                      <div className="mt-1">
                        <button
                          onClick={() => toggleDoneChildrenVisible(parent.id)}
                          className="text-xs text-neutral-400"
                        >
                          {expandedDoneChildren[parent.id] ? "Hide" : "Show"}{" "}
                          {doneChildren.length} completed
                        </button>
                        {expandedDoneChildren[parent.id] && (
                          <TodoList
                            todos={doneChildren}
                            onToggle={(t) => updateTodo(t, { done: !t.done })}
                            onDelete={deleteTodo}
                            onEdit={editTodo}
                            empty=""
                          />
                        )}
                      </div>
                    )}
                    <form onSubmit={(e) => addChild(parent, e)} className="flex gap-2 mt-2">
                      <input
                        className="flex-1 border rounded px-2 py-1 text-sm"
                        placeholder="Add a step…"
                        value={childDrafts[parent.id] || ""}
                        onChange={(e) =>
                          setChildDrafts((d) => ({ ...d, [parent.id]: e.target.value }))
                        }
                      />
                      <button className="text-sm bg-neutral-100 rounded px-2">Add</button>
                    </form>
                  </>
                )}
              </section>
            );
          })}

          <section>
            <button
              onClick={() => setShowCompleted((s) => !s)}
              className="text-sm font-semibold text-neutral-500"
            >
              <span className="text-xs">{showCompleted ? "▾" : "▸"}</span> Completed (
              {completedTopLevel.length})
            </button>
            {showCompleted && (
              <div className="mt-2">
                {!completedTopLevel.length ? (
                  <p className="text-sm text-neutral-400">Nothing completed yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {completedTopLevel.map((item) => {
                      const children = childrenOf(item.id);
                      return (
                        <li key={item.id} className="bg-white border rounded">
                          <div className="flex items-center gap-2 px-3 py-2">
                            <input
                              type="checkbox"
                              checked={item.done}
                              onChange={() => updateTodo(item, { done: !item.done })}
                            />
                            <EditableTodo
                              todo={item}
                              onEdit={editTodo}
                              className="text-sm line-through text-neutral-400"
                            />
                            {item.doneAt && (
                              <span className="text-xs text-neutral-400">
                                {formatDate(item.doneAt)}
                              </span>
                            )}
                            {children.length > 0 && (
                              <button
                                onClick={() => toggleCompletedChildrenVisible(item.id)}
                                className="text-xs text-neutral-400"
                              >
                                {expandedCompletedChildren[item.id] ? "hide" : "show"} steps
                              </button>
                            )}
                            <button
                              onClick={() => deleteTodo(item)}
                              className="text-xs text-neutral-400"
                            >
                              delete
                            </button>
                          </div>
                          {expandedCompletedChildren[item.id] && children.length > 0 && (
                            <div className="px-3 pb-2">
                              <TodoList
                                todos={children}
                                onToggle={(t) => updateTodo(t, { done: !t.done })}
                                onDelete={deleteTodo}
                                onEdit={editTodo}
                                empty=""
                              />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </section>

          <AdminFeedbackPanel />
        </>
      )}

      <FeedbackWidget />
    </div>
    </div>
  );
};

type TodoEditUpdates = { title: string; priority: number | null; complexity: number | null };

const TodoList = ({
  todos,
  onToggle,
  onDelete,
  onSchedule,
  onUnschedule,
  onEdit,
  onReset,
  getParentTitle,
  getContextTag,
  today,
  empty,
}: {
  todos: TodoData[];
  onToggle: (todo: TodoData) => void;
  onDelete: (todo: TodoData) => void;
  onSchedule?: (todo: TodoData) => void;
  onUnschedule?: (todo: TodoData) => void;
  onEdit: (todo: TodoData, updates: TodoEditUpdates) => void;
  onReset?: (todo: TodoData) => void;
  getParentTitle?: (todo: TodoData) => string | undefined;
  getContextTag?: (todo: TodoData) => string | undefined;
  today?: string;
  empty: string;
}) => {
  if (!todos.length) {
    return <p className="text-sm text-neutral-400">{empty}</p>;
  }
  return (
    <ul className="space-y-1">
      {todos.map((todo) => {
        const parentTitle = getParentTitle?.(todo);
        const contextTag = getContextTag?.(todo);
        // An item can be scheduled but overdue (rolled over from a past
        // date, still shown wherever it already lived) — "bump" it back to
        // literal today, which is also the one honest "deferred again"
        // signal (see the "Needs attention" section this feeds).
        const isOverdue = !!(today && todo.scheduledDate && todo.scheduledDate < today);
        const canSchedule = onSchedule && !todo.done && (!todo.scheduledDate || isOverdue);
        return (
          <li
            key={todo.id}
            className="flex items-center gap-2 bg-white border rounded px-3 py-2"
          >
            <input
              type="checkbox"
              checked={todo.done}
              onChange={() => onToggle(todo)}
            />
            <EditableTodo
              todo={todo}
              onEdit={onEdit}
              className={`text-sm ${todo.done ? "line-through text-neutral-400" : ""}`}
            />
            {contextTag && (
              <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5 capitalize">
                {contextTag}
              </span>
            )}
            {parentTitle && (
              <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5">
                in {parentTitle}
              </span>
            )}
            {todo.timesScheduled >= 2 && (
              <span className="text-xs bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                deferred {todo.timesScheduled}×
              </span>
            )}
            {todo.done && todo.doneAt && (
              <span className="text-xs text-neutral-400">{formatDate(todo.doneAt)}</span>
            )}
            {canSchedule && (
              <button
                onClick={() => onSchedule!(todo)}
                className="text-xs text-neutral-400"
              >
                {todo.scheduledDate ? "still today" : "today"}
              </button>
            )}
            {onUnschedule && !todo.done && todo.scheduledDate && (
              <button
                onClick={() => onUnschedule(todo)}
                className="text-xs text-neutral-400"
              >
                remove
              </button>
            )}
            {onReset && (
              <button
                onClick={() => onReset(todo)}
                className="text-xs text-neutral-400"
              >
                reset
              </button>
            )}
            <button
              onClick={() => onDelete(todo)}
              className="text-xs text-neutral-400"
            >
              delete
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const PRIORITY_LABELS: Record<number, string> = { 1: "P1", 2: "P2", 3: "P3" };
const COMPLEXITY_LABELS: Record<number, string> = { 1: "S", 2: "M", 3: "L" };

// Shared by every place a title shows (todos, projects/parents alike) — an
// inline rename+refine with explicit save/cancel rather than relying on
// blur/Enter, which get ambiguous about double-firing a save.
const EditableTodo = ({
  todo,
  onEdit,
  className,
}: {
  todo: TodoData;
  onEdit: (todo: TodoData, updates: TodoEditUpdates) => void;
  className?: string;
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(todo.title);
  const [priority, setPriority] = useState<number | undefined>(todo.priority);
  const [complexity, setComplexity] = useState<number | undefined>(todo.complexity);

  const startEditing = () => {
    setValue(todo.title);
    setPriority(todo.priority);
    setComplexity(todo.complexity);
    setEditing(true);
  };

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = value.trim();
          if (!trimmed) return;
          onEdit(todo, {
            title: trimmed,
            priority: priority ?? null,
            complexity: complexity ?? null,
          });
          setEditing(false);
        }}
        className="flex-1 flex flex-wrap items-center gap-1 min-w-0"
      >
        <input
          className="flex-1 min-w-[8rem] text-sm border rounded px-1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
        <select
          value={priority ?? ""}
          onChange={(e) => setPriority(e.target.value ? Number(e.target.value) : undefined)}
          className="text-xs border rounded shrink-0"
        >
          <option value="">P</option>
          <option value="1">P1</option>
          <option value="2">P2</option>
          <option value="3">P3</option>
        </select>
        <select
          value={complexity ?? ""}
          onChange={(e) => setComplexity(e.target.value ? Number(e.target.value) : undefined)}
          className="text-xs border rounded shrink-0"
        >
          <option value="">C</option>
          <option value="1">S</option>
          <option value="2">M</option>
          <option value="3">L</option>
        </select>
        <button type="submit" className="text-xs text-neutral-500 shrink-0">
          save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-xs text-neutral-400 shrink-0"
        >
          cancel
        </button>
      </form>
    );
  }

  return (
    <div className="flex-1 flex items-center gap-1 min-w-0">
      <span className={`truncate ${className || ""}`}>{todo.title}</span>
      {todo.priority && (
        <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1 py-0.5 shrink-0">
          {PRIORITY_LABELS[todo.priority]}
        </span>
      )}
      {todo.complexity && (
        <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1 py-0.5 shrink-0">
          {COMPLEXITY_LABELS[todo.complexity]}
        </span>
      )}
      <button onClick={startEditing} className="text-xs text-neutral-400 shrink-0">
        edit
      </button>
    </div>
  );
};
